import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectorRef, ChangeDetectionStrategy, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { K3OverridesService } from './k3-overrides.service';
import * as _ from 'lodash-es';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { JsonObjectFactoryService } from '../../../shared/_services/json-object-factory.service';
import { ConfirmationModalConfig } from '../../../shared/components/confirmation-modal/confirmation-modal.component';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-k3-overrides',
    templateUrl: './k3-overrides.component.html',
    styleUrls: ['./k3-overrides.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class K3OverridesComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private isInitialized = false;
  private readonly subs = new Subscription();
  private pendingConfirmAction: (() => void) | null = null;

  filterParams: any = {};
  rowData: any = {};
  globalService: any = {};
  USER_SETTINGS: any = {};
  GENERAL_CONFIG: any = {};

  filteredTaxYearData: any[] = [];
  selectedTaxYear = '';
  jcd_key = '';
  logged_in_user = '';
  loading = true;
  loadingScenario = false;
  loadingForm = false;
  loadParentMEDropdown = false;
  loadingParentME = false;
  loadingPship = false;
  selectedEnableDisable = '';

  scenarioDropDown: any[] = [];
  selectedScenario = '';
  getForm: any[] = [];
  selectedForm = '';
  parentMEDropDown: any[] = [];
  selectedParentMes = '';
  getPshipDtls: any[] = [];
  selectedPship = '';
  showPship = false;

  showConfirmDialog = false;
  confirmModalConfig: ConfirmationModalConfig = {
    title: 'Save K3 override process',
    message: 'Are you sure you want to save K3 override process?',
    iconClass: 'fa fa-sign-out',
    iconColor: 'txt-color-orange-dark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    confirmButtonClass: 'btn-danger',
    cancelButtonClass: 'btn-default',
    showCancelButton: true,
    showConfirmButton: true,
  };

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private cdr: ChangeDetectorRef,
    private gtwSnackbarService: GtwSnackbarService,
    private k3OverridesService: K3OverridesService,
    private jsonObjectFactory: JsonObjectFactoryService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.hideWorkspaceFilters();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.isInitialized = false;
      this.tryInit();
      this.cdr.markForCheck();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.isInitialized = false;
      this.tryInit();
      this.cdr.markForCheck();
    }));
    this.tryInit();
  }

  ngOnDestroy(): void {
    this.gtwWorkspace.hideDataFilters.next(false);
    this.subs.unsubscribe();
  }

  private hideWorkspaceFilters(): void {
    this.gtwWorkspace.hideDataFilters.next(true);
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const fp = normalizeFilterParams(activeScreen) || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    const gc = generalConfigFromGlobal(this.gtwGlobal) || {};
    this.GENERAL_CONFIG = { ...gc, base_url: gc.base_url ?? gc.api ?? '' };
    this.globalService = this.gtwGlobal.hydrateGlobalParamsFromContext(activeScreen, fp) || {};
    this.USER_SETTINGS = header.userSettingsData || {};
    this.filterParams = fp;
    this.rowData = rowPayloadFromActiveScreen(activeScreen) || {};
  }

  private tryInit(): void {
    if (this.isInitialized) return;
    if (!this.GENERAL_CONFIG?.base_url) return;
    this.isInitialized = true;
    this.init();
  }

  private init(): void {
    this.jcd_key = this.globalService?.jcd_key || this.filterParams?.jcd_key || '';
    this.logged_in_user = this.USER_SETTINGS?.user?.sso_id || '';
    this.getTaxYearDropDown();
  }

  getTaxYearDropDown(): void {
    this.selectedTaxYear = '';
    this.loading = true;
    const params = { action_code: 'e83e1x' };
    const globalServiceWrapper = { globalParams: this.globalService };

    this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadJsonObject',
      params,
      globalServiceWrapper,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        const taxYearData = _.filter(data.jsonObject, (p: any) => _.includes(['TAX_YEAR'], p.CODE_GROUP));
        this.filteredTaxYearData = [];
        const curentyear = this.globalService?.tax_year || this.filterParams?.tax_year || '';
        for (const indx in taxYearData) {
          if (taxYearData[indx].CODE_GROUP === 'TAX_YEAR' &&
            (taxYearData[indx].CODE_DISP_DESC <= curentyear &&
              taxYearData[indx].CODE_DISP_DESC >= (parseInt(curentyear, 10) - 5))) {
            this.filteredTaxYearData.push(taxYearData[indx]);
          }
        }
        this.filteredTaxYearData.sort((a, b) => parseInt(b.CODE_DISP_DESC, 10) - parseInt(a.CODE_DISP_DESC, 10));
        this.loading = false;
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error loading tax year dropdown:', error);
        this.loading = false;
        this.cdr.detectChanges();
      }
    );
  }

  getScenarioDropDown(): void {
    this.selectedScenario = '';
    this.scenarioDropDown = [];
    this.loadParentMEDropdown = false;
    this.showPship = false;
    this.selectedForm = '';
    this.getForm = [];
    this.selectedParentMes = '';
    this.selectedPship = '';
    this.getPshipDtls = [];
    this.parentMEDropDown = [];

    if (!this.selectedTaxYear) {
      this.gtwSnackbarService.openSnackBar('Please select the Tax Year to get the Filing Group.', 'gtw-snackbar--red');
      return;
    }

    this.loadingScenario = true;
    this.cdr.detectChanges();
    const params = { action_code: 'va45va', p_tax_year: this.selectedTaxYear, p_jcd_key: this.jcd_key };
    const globalServiceWrapper = { globalParams: this.globalService };

    this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadJsonObject',
      params,
      globalServiceWrapper,
      null,
      null
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.scenarioDropDown = data.jsonObject || [];
        this.loadingScenario = false;
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error loading scenario dropdown:', error);
        this.loadingScenario = false;
        this.cdr.detectChanges();
      }
    );
  }

  getFromDropdown(): void {
    this.selectedForm = '';
    this.getForm = [];
    this.loadParentMEDropdown = false;
    this.showPship = false;
    this.selectedParentMes = '';
    this.selectedPship = '';
    this.getPshipDtls = [];
    this.parentMEDropDown = [];

    if (!this.selectedScenario) {
      return;
    }

    this.loadingForm = true;
    this.cdr.detectChanges();
    const params = { action_code: 'abkcdl' };
    const globalServiceWrapper = { globalParams: this.globalService };

    this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadJsonObject',
      params,
      globalServiceWrapper,
      null,
      null
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.getForm = data.jsonObject || [];
        _.remove(this.getForm, { PRIORITY_ORDER: 1 });
        this.loadingForm = false;
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error loading form dropdown:', error);
        this.loadingForm = false;
        this.cdr.detectChanges();
      }
    );
  }

  getParentMeDropdown(): void {
    this.selectedParentMes = '';
    this.parentMEDropDown = [];
    this.loadParentMEDropdown = false;
    this.showPship = false;
    this.selectedPship = '';
    this.getPshipDtls = [];

    if (!this.selectedTaxYear) {
      this.gtwSnackbarService.openSnackBar('Please select the Tax Year to save the Apportionment.', 'gtw-snackbar--red');
      return;
    }

    this.loadingParentME = true;
    const params = {
      action_code: '07roqs',
      sso_id: this.logged_in_user,
      p_tax_year: this.selectedTaxYear,
      p_scenario: this.selectedScenario
    };
    const globalServiceWrapper = { globalParams: this.globalService };

    this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadJsonObject',
      params,
      globalServiceWrapper,
      null,
      null
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.parentMEDropDown = _.filter(data.jsonObject || [], (d: any) => d.LEVEL_GROUP === 'Parent ME');
        this.loadParentMEDropdown = true;
        this.loadingParentME = false;
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error loading parent ME dropdown:', error);
        this.loadingParentME = false;
        this.cdr.detectChanges();
      }
    );
  }

  getPshipDropdown(): void {
    this.selectedPship = '';
    this.showPship = false;

    if (!this.selectedParentMes) {
      return;
    }

    this.loadingPship = true;
    this.cdr.detectChanges();
    const params = {
      action_code: 'kae9bk',
      tax_year: this.selectedTaxYear,
      scenario: this.selectedScenario,
      jcd_key: this.jcd_key,
      p_form: this.selectedForm,
      p_schedule: 'K3',
      parent_me_string: this.selectedParentMes
    };
    const globalServiceWrapper = { globalParams: this.globalService };

    this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadJsonObject',
      params,
      globalServiceWrapper,
      null,
      null
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.getPshipDtls = data.jsonObject || [];
        _.remove(this.getPshipDtls, { VALUE: -1 });
        this.showPship = true;
        this.loadingPship = false;
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error loading partnership dropdown:', error);
        this.loadingPship = false;
        this.cdr.detectChanges();
      }
    );
  }

  parentMeKey(option: any): string {
    return option.PARENT_ME_KEY ?? option.VALUE ?? '';
  }

  parentMeLabel(option: any): string {
    return option.PARENT_ME_NAME ?? option.LABEL ?? '';
  }

  onSaveK3Click(): void {
    if (!this.selectedTaxYear) {
      this.gtwSnackbarService.openSnackBar('Please select the Tax Year to save the sync process.', 'gtw-snackbar--red');
      return;
    }
    if (!this.selectedScenario) {
      this.gtwSnackbarService.openSnackBar('Please select the Scenario to save the sync process.', 'gtw-snackbar--red');
      return;
    }
    if (!this.selectedForm) {
      this.gtwSnackbarService.openSnackBar('Please select the Form to save the sync process.', 'gtw-snackbar--red');
      return;
    }
    if (!this.selectedEnableDisable) {
      this.gtwSnackbarService.openSnackBar('Please select Enable/Disable to save the sync process.', 'gtw-snackbar--red');
      return;
    }
    if (!this.selectedParentMes) {
      this.gtwSnackbarService.openSnackBar('Please select the ME(s) to save the sync process.', 'gtw-snackbar--red');
      return;
    }
    if (!this.selectedPship) {
      this.gtwSnackbarService.openSnackBar('Please select the Partnership to save the sync process.', 'gtw-snackbar--red');
      return;
    }
    const pshipItem = this.getPshipDtls.find(item => item.VALUE == this.selectedPship);
    if (pshipItem && pshipItem.COMP_DEFAULT === 'N') {
      this.gtwSnackbarService.openSnackBar('Please set the Component Defaults before saving the K3 Sync.', 'gtw-snackbar--red');
      return;
    }

    this.confirmAction(
      'Save K3 override process',
      'Are you sure you want to save K3 override process?',
      () => this.runSyncProcess()
    );
  }

  private confirmAction(title: string, message: string, action: () => void): void {
    this.confirmModalConfig = { ...this.confirmModalConfig, title, message };
    this.pendingConfirmAction = action;
    this.showConfirmDialog = true;
    this.cdr.detectChanges();
  }

  onConfirmYes(): void {
    this.showConfirmDialog = false;
    if (this.pendingConfirmAction) {
      this.pendingConfirmAction();
      this.pendingConfirmAction = null;
    }
    this.cdr.detectChanges();
  }

  onConfirmNo(): void {
    this.showConfirmDialog = false;
    this.pendingConfirmAction = null;
    this.cdr.detectChanges();
  }

  runSyncProcess(): void {
    const scenarioCode = this.globalService?.scenarioCode || this.filterParams?.scenarioCode || 'RAF';
    const isIssueKeyEnabled = this.globalService?.is_issue_key_enabled || this.filterParams?.is_issue_key_enabled || 'N';
    const pshipItem = this.getPshipDtls.find(item => item.VALUE == this.selectedPship);

    const params = {
      sso_id: this.USER_SETTINGS?.user?.sso_id,
      tax_year: this.selectedTaxYear,
      scenario: this.selectedScenario,
      jcd_key: this.globalService?.jcd_key || this.jcd_key,
      scenarioCode,
      is_issue_key_enabled: isIssueKeyEnabled,
      p_form: this.selectedForm,
      p_filer_key: this.selectedPship,
      p_flag: this.selectedEnableDisable,
      p_filer_label: pshipItem?.LABEL || ''
    };
    const lockInfo = {
      tax_year: this.selectedTaxYear,
      scenario: this.selectedScenario,
      jcd_key: this.jcd_key,
      combination_key: this.selectedPship
    };
    const saveParams = { ...params, checkLockedbj: JSON.stringify([lockInfo]) };

    this.k3OverridesService.saveK3Sync(
      this.GENERAL_CONFIG.base_url,
      saveParams,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (response.callSuccess != 1) {
          this.gtwSnackbarService.openSnackBar(response.errorMessage, 'gtw-snackbar--red');
          return;
        }
        this.gtwSnackbarService.openSnackBar('Sync Process for K3 is initiated.', 'gtw-snackbar--green');
        this.requestGridUpdate.emit('refresh');
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error initiating K3 sync:', error);
        this.gtwSnackbarService.openSnackBar('Error occurred while initiating sync process. Please try again.', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      }
    );
  }

  reset(): void {
    this.selectedTaxYear = '';
    this.selectedScenario = '';
    this.scenarioDropDown = [];
    this.getForm = [];
    this.selectedForm = '';
    this.loadParentMEDropdown = false;
    this.parentMEDropDown = [];
    this.selectedParentMes = '';
    this.getPshipDtls = [];
    this.selectedPship = '';
    this.showPship = false;
    this.selectedEnableDisable = '';
    this.loadingScenario = false;
    this.loadingForm = false;
    this.loadingParentME = false;
    this.loadingPship = false;
    this.cdr.detectChanges();
  }
}
